import {
  type PlayerProfileResponse,
  type PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  type PublicMeasurementResponseRecord,
  rdbV1PeopleMeasurementsAveragesDetail,
  rdbV1PeopleMeasurementsDetail,
  rdbV1PlayerProfileDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { PlayerVitalsList } from 'components/Db/Profile/PlayerVitalsList';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { useMemo } from 'react';

export interface IPlayerVitalsPageProps {
  basicInfo: PlayerProfileResponse;
  sectionTitle: string;
  playerVitals?: {
    playerMeasurements: PublicMeasurementResponseRecord[];
  };
  vitalAverages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData;
}

interface IPlayerVitalsPage extends IPlayerVitalsPageProps, IGenericPageProps {}

const PlayerVitalsPage = ({
  playerVitals,
  vitalAverages,
  basicInfo,
  ...props
}: IPlayerVitalsPage) => {
  const data = useMemo(() => {
    return {
      athleteData: basicInfo,
    };
  }, [basicInfo]);

  const title = `${basicInfo?.name} - Vitals`;
  const sectionTitle = 'Vitals';
  const description = `Latest Vitals for ${basicInfo?.name}.`;
  const defaultAsset = basicInfo?.defaultAsset;
  const noIndex = playerVitals?.playerMeasurements?.length === 0;

  const image = useMemo(() => {
    const defaultImage = { height: 200, src: '', width: 200 };

    if (!defaultAsset) {
      return defaultImage;
    }

    const { domain, source, height, width } = defaultAsset;
    const src = (domain && source && `https://${domain}${source}`) || '';

    return { height: height || 200, src, width: width || 200 };
  }, [defaultAsset]);

  return (
    <AthleteProvider data={data}>
      <PageWrapper
        {...props}
        description={description}
        image={image}
        noIndex={noIndex}
        ogType="athlete"
        title={title}
      >
        <PlayerVitalsList
          playerVitals={playerVitals}
          sectionTitle={sectionTitle}
          vitalAverages={vitalAverages}
        />
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query;
  const personKey = id?.toString().split('-').pop();

  if (!personKey) {
    return {
      notFound: true,
    };
  }

  let data = {};

  try {
    const params = { personKey: +personKey };
    const [player] = await Promise.allSettled([
      rdbV1PlayerProfileDetail(proxyApi, params),
    ]);

    if (player.status !== 'fulfilled') {
      return {
        notFound: true,
      };
    }

    const fetchedPlayer = player.value;

    const [playerVitals, vitalAverages] = await Promise.allSettled([
      rdbV1PeopleMeasurementsDetail(proxyApi, params),
      rdbV1PeopleMeasurementsAveragesDetail(proxyApi, params),
    ]);

    data = {
      siteData,
      pageType: 'database',
      userData: userData || [],
      basicInfo: fetchedPlayer,
      playerVitals: getFulfilledValue(playerVitals),
      vitalAverages: getFulfilledValue(vitalAverages),
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(PlayerVitalsPage);
